import { differenceInDays } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { Purchases, PurchasesOfferings, CustomerInfo, IntroEligibility } from '@revenuecat/purchases-capacitor';
import { posthog } from 'posthog-js';

import { pick, pickBy } from 'lodash';
import { Store } from 'pullstate';
import { SortingRule } from 'react-table';
import { InAppReview } from '@capacitor-community/in-app-review';
import { FilterKey, ColumnSelection, ColumnSelectionType } from './components/InvestmentTable';
import { TimeRange } from './types';
import * as storage from './storage';
import { waitForPurchases } from './utils/purchasesInit';
export { Portfolios, initializePortfolios } from './storage';
import { PushNotificationService } from './services/pushNotificationService';

const getWatchlistFromLocalstorageAndRemove = () => {
  const watchlistString = window.localStorage.getItem('watchlist');
  if (!watchlistString) return;
  const watchlist = JSON.parse(watchlistString);
  window.localStorage.removeItem('watchlist');
  return watchlist;
};

export const generalStoreId = 'GeneralStore';
interface General {
  isAppReady: boolean;
  appOpenDates?: string[];
  lastReviewPromptAttemptDate?: string;
  sessionWatchlistViewCount: number;
  watchlist: { [symbol: string]: boolean };
  isSubscriptionTakeoverShown: boolean;
  hasSeenSubscriptionTakeoverThisSession: boolean;
  isSearchOnboardingPopoverShown: boolean;
  searchOnboardingPopoverOpenedAt?: string;
  isBookmarkOnboardingPopoverShown: boolean;
  bookmarkOnboardingPopoverOpenedAt?: string;
  hasSeenPortfoliosSectionIntro: boolean;
  hasSeenWatchlistSectionIntro: boolean;
  isKeyboardVisible: boolean;
  keyboardHeight: number;
  isPaymentModalByPreferenceShown: boolean;
  paymentModalSource?: string;
}
export type SavedGeneral = Pick<
  General,
  | 'appOpenDates'
  | 'lastReviewPromptAttemptDate'
  | 'watchlist'
  | 'searchOnboardingPopoverOpenedAt'
  | 'bookmarkOnboardingPopoverOpenedAt'
  | 'hasSeenPortfoliosSectionIntro'
  | 'hasSeenWatchlistSectionIntro'
>;
export const General = new Store<General>({
  isAppReady: false,
  sessionWatchlistViewCount: 0,
  watchlist: {},
  isSubscriptionTakeoverShown: false,
  hasSeenSubscriptionTakeoverThisSession: false,
  isSearchOnboardingPopoverShown: false,
  isBookmarkOnboardingPopoverShown: false,
  hasSeenPortfoliosSectionIntro: false,
  hasSeenWatchlistSectionIntro: false,
  isKeyboardVisible: false,
  keyboardHeight: 0,
  isPaymentModalByPreferenceShown: false,
});
export const initializeGeneral = async () => {
  General.createReaction(
    (s) => s,
    (store, draft) => {
      storage.set(
        generalStoreId,
        pick(store, [
          'appOpenDates',
          'lastReviewPromptAttemptDate',
          'watchlist',
          'searchOnboardingPopoverOpenedAt',
          'bookmarkOnboardingPopoverOpenedAt',
          'hasSeenPortfoliosSectionIntro',
          'hasSeenWatchlistSectionIntro',
        ]),
      );

      const enoughDaysSinceFirstOpen =
        store.appOpenDates?.[0] && differenceInDays(new Date(), new Date(store.appOpenDates[0])) > 2;
      const enoughAppOpens = store.appOpenDates && store.appOpenDates.length > 2;
      const enoughDaysSinceLastPrompt =
        !store.lastReviewPromptAttemptDate ||
        differenceInDays(new Date(), new Date(store.lastReviewPromptAttemptDate)) >= 5;

      if (
        Capacitor.isPluginAvailable('RateApp') &&
        enoughDaysSinceFirstOpen &&
        enoughAppOpens &&
        enoughDaysSinceLastPrompt
      ) {
        InAppReview.requestReview();
        posthog.capture('Attempted to ask for review.');
        draft.lastReviewPromptAttemptDate = new Date().toISOString();
      }
    },
  );

  General.createReaction(
    (s) => JSON.stringify(s.watchlist), // Cambiamos para detectar cambios en el objeto
    (watchlistStr) => {
      console.log('[NOTIFICATIONS] Watchlist changed:', watchlistStr); // Debug log
      const watchlist = JSON.parse(watchlistStr);

      const savedPreferences = localStorage.getItem('notificationPreferences');
      if (!savedPreferences) {
        console.log('[NOTIFICATIONS] No preferences found');
        return;
      }

      try {
        const preferences = JSON.parse(savedPreferences);
        const enabledTypes = preferences
          .filter((pref: { enabled: boolean }) => pref.enabled)
          .map((pref: { id: string }) => pref.id);

        console.log('[NOTIFICATIONS] Enabled types:', enabledTypes);

        // Only update config if there are enabled notification types
        // This prevents automatic permission requests when no notifications are enabled
        if (enabledTypes.length === 0) {
          console.log('[NOTIFICATIONS] No enabled notifications, skipping config update');
          return;
        }

        const watchlistSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
        console.log('[NOTIFICATIONS] Active symbols:', watchlistSymbols);

        PushNotificationService.updateConfig({
          notificationTypes: enabledTypes,
          watchlistSymbols,
        }).catch((error) => {
          console.error('[NOTIFICATIONS] Update failed:', error);
        });
      } catch (error) {
        console.error('[NOTIFICATIONS] Error processing preferences:', error);
      }
    },
  );

  const saved = await storage.get<SavedGeneral>(generalStoreId);
  const localStorageWatchlist = getWatchlistFromLocalstorageAndRemove();

  General.update((s) => {
    s.appOpenDates = saved?.appOpenDates
      ? saved.appOpenDates.concat(new Date().toISOString())
      : [new Date().toISOString()];
    s.lastReviewPromptAttemptDate = saved?.lastReviewPromptAttemptDate;
    s.watchlist = saved?.watchlist || localStorageWatchlist || {};
    s.searchOnboardingPopoverOpenedAt = saved?.searchOnboardingPopoverOpenedAt;
    s.bookmarkOnboardingPopoverOpenedAt = saved?.bookmarkOnboardingPopoverOpenedAt;
    s.hasSeenPortfoliosSectionIntro = saved?.hasSeenPortfoliosSectionIntro ?? false;
    s.hasSeenWatchlistSectionIntro = saved?.hasSeenWatchlistSectionIntro ?? false;
  });
};

const tableOptionsStoreId = 'TableOptions';
type TableOptions =
  | {
      [id: string]: {
        sortingRule?: SortingRule<string>;
        columnSelections?: ColumnSelection[];
        filters?: Partial<Record<FilterKey, boolean>>;
        selectedMetric?: string; // Add this line for the new metric selector
      };
    }
  | Record<string, never>;
export const TableOptions = new Store<TableOptions>({
  portfolio: {
    selectedMetric: 'latestPrice', // Set default metric
  },
});

export const initializeTableOptions = async () => {
  TableOptions.subscribe(
    (s) => s,
    (store) => {
      storage.set(tableOptionsStoreId, store);
    },
  );

  const saved = await storage.get<TableOptions>(tableOptionsStoreId);
  if (saved) {
    const savedWithInvalidSelectionsOmitted = pickBy(saved, ({ columnSelections }) => {
      if (!columnSelections) return true;
      return columnSelections.every(
        ({ type, timeRange }) =>
          !timeRange ||
          (Object.values(TimeRange).includes(timeRange) && Object.values(ColumnSelectionType).includes(type)),
      );
    });
    TableOptions.replace(savedWithInvalidSelectionsOmitted);
    TableOptions.update(() => {
      // trigger blank update for replace
    });
  }
};

// Payment Modal By Preference utilities
export const ONBOARDING_PAYWALL_SOURCES = [
  'onboarding_completion',
  'onboarding_skip',
  'onboarding_portfolio_skip',
  'onboarding_portfolio_continue',
] as const;

export const showPaymentModalByPreference = (source?: string) => {
  General.update((s) => {
    s.isPaymentModalByPreferenceShown = true;
    s.paymentModalSource = source;
  });

  // Track paywall open event
  try {
    posthog.capture('Paywall Opened', {
      source: source || 'unknown',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to track paywall open event:', error);
  }
};

export const hidePaymentModalByPreference = () => {
  General.update((s) => {
    s.isPaymentModalByPreferenceShown = false;
    s.paymentModalSource = undefined;
  });
};

export const entitlementIdentifier = 'full';
const purchaseStoreId = 'PurchaseStore';
interface PurchaseState {
  // Keep it named purchaserInfo for backwards compatibility
  purchaserInfo?: CustomerInfo;
  offerings?: PurchasesOfferings;
  /**
   * Tracks whether fresh subscription data has been loaded from RevenueCat.
   * Prevents race conditions where cached data might be stale.
   * Always false on app start, set to true after API call completes.
   */
  isPurchaseInfoLoaded: boolean;
  /**
   * Trial eligibility status for products. Used to show accurate
   * "Free for 3 days" messaging only to users who are actually eligible.
   * Users who previously subscribed will see non-trial pricing.
   */
  trialEligibility?: { [productId: string]: IntroEligibility };
}
export const Purchase = new Store<PurchaseState>({
  isPurchaseInfoLoaded: false,
});
Purchase.subscribe(
  (s) => s,
  (store) => {
    storage.set(purchaseStoreId, store);
  },
);

export const initializePurchase = async () => {
  const savedPurchaseStore = await storage.get<PurchaseState>(purchaseStoreId);

  if (savedPurchaseStore) {
    Purchase.update((s) => {
      s.offerings = savedPurchaseStore.offerings;
      s.purchaserInfo = savedPurchaseStore.purchaserInfo;
      s.trialEligibility = savedPurchaseStore.trialEligibility;
      // Always reset to false on app start to force fresh data fetch from RevenueCat
      // This ensures we always have the latest subscription status
      s.isPurchaseInfoLoaded = false;
    });
  }

  (async () => {
    let offerings: PurchasesOfferings | undefined;
    let purchaserInfo: CustomerInfo | undefined;

    try {
      if (Capacitor.isNativePlatform()) {
        // Ensure configure() has finished (incl. Android BillingClient connection)
        // before calling any SDK method — avoids PURCHASE_NOT_ALLOWED_ERROR race condition
        await waitForPurchases();
        [offerings, purchaserInfo] = await Promise.all([
          Purchases.getOfferings(),
          (await Purchases.getCustomerInfo()).customerInfo,
        ]);
      } else {
        // Web platform: purchaserInfo stays undefined (no RevenueCat on web)
        // This is intentional - web users can't subscribe, so isSubscribed will be false
        /*
        purchaserInfo = {
          entitlements: {
            active: {
              [entitlementIdentifier]: {
                isActive: true,
              },
            },
          },
        } as any;
        */

        // placeholder offerings
        offerings = {
          current: {
            weekly: {
              identifier: '$rc_weekly',
              packageType: 'WEEKLY',
              product: {
                price: 5,
                price_string: '$5',
              },
            },
            annual: {
              identifier: '$rc_annual',
              packageType: 'ANNUAL',
              product: {
                price: 104,
                price_string: '$104',
                intro_price: 0,
                intro_price_period_unit: 'DAY',
                intro_price_period_number_of_units: 3,
              },
            },
          },
          // Add special discount offering
          one_time_discount: {
            annual: {
              identifier: '$rc_annual',
              packageType: 'ANNUAL',
              product: {
                price: 78,
                price_string: '$78',
                title: 'Annual Subscription (25% off)',
                // Monthly equivalent for display purposes
                weekly_equivalent: 1.5,
              },
            },
          },
        } as any;
      }
    } catch (error) {
      console.error('Failed to fetch RevenueCat data, using cached data:', error);
      // Don't update offerings/purchaserInfo - keep cached values from savedPurchaseStore
      // This allows graceful degradation when RevenueCat API is unreachable
    } finally {
      // CRITICAL: Only mark as loaded when we have reliable subscription data
      // This prevents showing paywall when we don't know subscription status
      Purchase.update((s) => {
        if (offerings) s.offerings = offerings;
        if (purchaserInfo !== undefined) s.purchaserInfo = purchaserInfo;

        // Web platform: Always mark as loaded (no RevenueCat, treat as non-subscriber)
        if (!Capacitor.isNativePlatform()) {
          s.isPurchaseInfoLoaded = true;
          return;
        }

        // Native platform: Check if we have any subscription data (fresh or cached)
        const hasSubscriptionData = purchaserInfo !== undefined || s.purchaserInfo !== undefined;

        if (hasSubscriptionData) {
          s.isPurchaseInfoLoaded = true;
          return;
        }

        // No subscription data at all (brand-new user on first launch with SDK failure).
        // Treat as non-subscriber rather than leaving them frozen forever.
        console.warn(
          '[Purchase Store] No subscription data available (new user + RevenueCat failure). ' +
            'Marking as loaded to prevent frozen onboarding. User treated as non-subscriber.',
        );
        s.isPurchaseInfoLoaded = true;
      });
    }
  })();
};
